import type jsPDF from "jspdf"

import { createJsPdf } from "@/app/report/common/export/lazyLibs"

let pretendardFontBase64: string | null = null
let fontRegistered = false

export const registerPretendardFont = async (doc: jsPDF): Promise<void> => {
  if (fontRegistered && pretendardFontBase64) {
    doc.addFileToVFS("Pretendard-Regular.ttf", pretendardFontBase64)
    doc.addFont("Pretendard-Regular.ttf", "pretendard", "normal")

    return
  }

  try {
    const fontUrl =
      "https://cdn.jsdelivr.net/npm/pretendard@latest/dist/public/static/Pretendard-Regular.otf"

    const response = await fetch(fontUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch font: ${response.status}`)
    }

    const arrayBuffer = await response.arrayBuffer()

    const uint8Array = new Uint8Array(arrayBuffer)
    let binary = ""
    for (let i = 0; i < uint8Array.length; i++) {
      binary += String.fromCharCode(uint8Array[i])
    }
    pretendardFontBase64 = btoa(binary)

    doc.addFileToVFS("Pretendard-Regular.ttf", pretendardFontBase64)
    doc.addFont("Pretendard-Regular.ttf", "pretendard", "normal")

    fontRegistered = true
  } catch (error) {
    console.error("Failed to register Pretendard font:", error)
  }
}

export class PDF {
  top: number
  doc: jsPDF

  page = {
    contentWidth: 194,
    pageWidth: 210,
    pageHeight: 210 * 1.414 - 10,
    paddingY: 10,
    paddingX: 10,
    gap: 20,
  }

  /**
   * jspdf 는 내보낼 때만 받아 온다 (#198). 생성자는 비동기일 수 없어 이 팩토리로 만든다 —
   * `new PDF()` 로는 문서를 만들 수 없다.
   */
  static async create(): Promise<PDF> {
    return new PDF(await createJsPdf())
  }

  constructor(doc: jsPDF) {
    this.top = this.page.paddingY
    this.doc = doc
  }

  save(filename: string) {
    this.doc.save(filename)
  }

  addPage() {
    this.doc.addPage()
  }

  addText(text: string, x: number, y: number, size = 12) {
    this.doc.setFontSize(size)
    this.doc.text(text, x, y)
  }

  addLine(x1: number, y1: number, x2: number, y2: number) {
    this.doc.line(x1, y1, x2, y2)
  }

  addRect(x: number, y: number, width: number, height: number) {
    this.doc.rect(x, y, width, height)
  }

  addImage(image: string, x: number, y: number, width: number, height: number) {
    this.doc.addImage(image, "PNG", x, y, width, height)
  }

  addCanvas(canvas: HTMLCanvasElement, x: number, y: number, width: number, height: number) {
    const img = canvas.toDataURL("image/png")
    this.doc.addImage(img, "PNG", x, y, width, height)
  }

  appendCanvas(canvas: HTMLCanvasElement) {
    const { width, height } = canvas
    const ratio = width / this.page.contentWidth
    const left = this.page.paddingX
    const imageWidth = width / ratio
    const imageHeight = height / ratio
    if (this.page.pageHeight < this.top + imageHeight) {
      this.top = this.page.paddingY
      this.doc.addPage()
    }
    this.addCanvas(canvas, left, this.top, imageWidth, imageHeight)
    this.top += imageHeight + this.page.gap
  }
}
